import { useState, useRef, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import ERPSnakBar from "../components/ERPSnakBar";
import { ColDef } from "ag-grid-community";
import ERPGrid from "../components/ERPGrid";
import { AgGridReact } from "ag-grid-react";
import { supplyStatus } from "../services/articleService";
import { streamShopifyProducts, streamShopifySales } from "../services/shopifyService";
import { ExportToExcel } from "../util/globals";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

function SupplyStatus() {
  
  const [loading, setLoading] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [syncedCount, setSyncedCount] = useState<number>(0);
  const [syncPhase, setSyncPhase] = useState<"products" | "sales">("products");
  const [list, setList] = useState<any[]>([]);
  const [pinnedBottomRows, setPinnedBottomRows] = useState<any[]>([]);
  const GridRef = useRef<AgGridReact>(null);
  const esRef = useRef<EventSource | null>(null);

  const [fromDate, setFromDate] = useState<string>(
    dayjs().subtract(1, "month").format("YYYY-MM-DD")
  );
  const [toDate, setToDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );

  useEffect(() => {
    return () => {
      esRef.current?.close();
    };
  }, []);

  const recalculateTotal = useCallback((event?: any) => {
    const api = event?.api ?? GridRef.current?.api;
    if (!api) return;
    let net_production_qty = 0;
    let transit_qty = 0;
    let in_hand_qty = 0;
    let sale_qty = 0;
    api.forEachNodeAfterFilter((node: any) => {
      if (node.rowPinned) return;
      net_production_qty += Number(node.data?.net_production_qty ?? 0);
      transit_qty += Number(node.data?.transit_qty ?? 0);
      in_hand_qty += Number(node.data?.in_hand_qty ?? 0);
      sale_qty += Number(node.data?.sale_qty ?? 0);
    });
    setPinnedBottomRows([{ barcode: "Total", net_production_qty, transit_qty, in_hand_qty, sale_qty }]);
  }, []);
  
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    serverity: "",
  });

  const columns: ColDef[] = [
    {
      field: "brand",
      headerName: "Brand",
      width: 130,
    },
    {
      field: "category",
      headerName: "Category",
      width: 150,
    },
    {
      filter: true,
      field: "art_no",
      headerName: "Article No",
      width: 130,
    },
    {
      filter: true,
      field: "art_name",
      headerName: "Article Name",
      width: 200,
    },
    {
      filter: true,
      field: "color",
      headerName: "Color",
      width: 150,
    },
    {
      filter: true,
      field: "size",
      headerName: "Size",
      width: 120,
    },
    {
      field: "barcode",
      headerName: "Barcode",
      width: 160,
      filter: true,
    },
    
    {
      filter: true,
      field: "net_production_qty",
      headerName: "Net Prod. Qty",
      minWidth: 160,
      flex: 1,
      valueGetter: (params: any) => Number(params.data?.net_production_qty ?? 0),
    },
    {
      filter: true,
      field: "transit_qty",
      headerName: "Transit Qty",
      minWidth: 150,
      flex: 1,
      valueGetter: (params: any) => Number(params.data?.transit_qty ?? 0),
    },
    {
      filter: true,
      field: "in_hand_qty",
      headerName: "In Stock Qty",
      minWidth: 150,
      flex: 1,
      valueGetter: (params: any) => Number(params.data?.in_hand_qty ?? 0),
    },
    {
      filter: true,
      field: "sale_qty",
      headerName: "Sale Qty",
      minWidth: 120,
      flex: 1,
      valueGetter: (params: any) => Number(params.data?.sale_qty ?? 0),
    },
  ];

 

  const handleExport = () => {
    const blob = ExportToExcel(list, "Supply Status");
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "supply_status.xlsx";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      
      const response: any = await supplyStatus();
     
      if (response.status) {
        setList(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setAlert({
        open: true,
        message: error?.response?.data?.message || "Error loading List",
        serverity: "error",
      });
      console.log(error);
    }
  };

  const handleSynchronize = () => {
    if (list.length === 0) {
      setAlert({ open: true, message: "Please refresh the list first", serverity: "warning" });
      return;
    }
    esRef.current?.close();
    setSyncing(true);
    setSyncedCount(0);
    setSyncPhase("products");

    const shopifyProducts: any[] = [];

    // Phase 1: fetch Shopify products → fill in_hand_qty by barcode
    esRef.current = streamShopifyProducts(
      (batch, totalSoFar) => {
        shopifyProducts.push(...batch);
        setSyncedCount(totalSoFar);
      },
      (total) => {
        setSyncedCount(total);
        const inventoryMap = new Map<string, number>();
        for (const p of shopifyProducts) {
          if (p.barcode) {
            inventoryMap.set(String(p.barcode), p.inventory_qty ?? 0);
          }
        }
        setList((prev) =>
          prev.map((row) => ({
            ...row,
            in_hand_qty: row.barcode && inventoryMap.has(String(row.barcode))
              ? inventoryMap.get(String(row.barcode))
              : row.in_hand_qty,
          }))
        );

        // Phase 2: fetch Shopify sales within date range → fill sale_qty by barcode
        setSyncPhase("sales");
        setSyncedCount(0);

        let latestSalesSnapshot: any[] = [];

        esRef.current = streamShopifySales(
          fromDate,
          toDate,
          (batch, totalSoFar) => {
            latestSalesSnapshot = batch; // always keep the latest full snapshot (not cumulative push)
            setSyncedCount(totalSoFar);
          },
          (_total) => {
            const saleMap = new Map<string, number>();
            for (const s of latestSalesSnapshot) {
              if (s.barcode) {
                const key = String(s.barcode);
                saleMap.set(key, (saleMap.get(key) ?? 0) + (s.sale_qty ?? 0));
              }
            }
            setList((prev) =>
              prev.map((row) => ({
                ...row,
                sale_qty: row.barcode && saleMap.has(String(row.barcode))
                  ? saleMap.get(String(row.barcode))
                  : row.sale_qty,
              }))
            );
            setSyncing(false);
          },
          (message) => {
            setSyncing(false);
            setAlert({ open: true, message, serverity: "error" });
          }
        );
      },
      (message) => {
        setSyncing(false);
        setAlert({ open: true, message, serverity: "error" });
      }
    );
  };

    return (
        <Box component={"div"} sx={{ mt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
                sx={{
                    mb: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    flexWrap: "wrap",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <DatePicker
                      label="From Date"
                      format="DD-MM-YYYY"
                      value={dayjs(fromDate)}
                      onChange={(newValue) => {
                        if (newValue) setFromDate(dayjs(newValue).format("YYYY-MM-DD"));
                      }}
                      slotProps={{ textField: { size: "small" } }}
                    />
                    <DatePicker
                      label="To Date"
                      format="DD-MM-YYYY"
                      value={dayjs(toDate)}
                      onChange={(newValue) => {
                        if (newValue) setToDate(dayjs(newValue).format("YYYY-MM-DD"));
                      }}
                      slotProps={{ textField: { size: "small" } }}
                    />
                    <Button
                        onClick={handleRefresh} variant="outlined" disabled={syncing}>
                        Refresh
                    </Button>
                    <Button
                        onClick={handleExport} variant="outlined" disabled={list.length === 0}>
                        Export to Excel
                    </Button>
                </Box>
                <Button
                    onClick={handleSynchronize} variant="outlined" disabled={syncing || loading}>
                    Synchronize
                </Button>
            </Box>
          </LocalizationProvider>

            {syncing && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {syncPhase === "products"
                      ? "Phase 1/2 — Fetching Shopify products..."
                      : "Phase 2/2 — Fetching Shopify sales..."}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {syncedCount.toLocaleString()} downloaded
                  </Typography>
                </Box>
                <LinearProgress />
              </Box>
            )}

            <Box>
        <ERPGrid
          gridHeight={"83vh"}
          exportToExcel={false}
          ref={GridRef}
          columnDefs={columns}
          rowData={list}
          pinnedBottomRowData={pinnedBottomRows}
          getRowStyle={(params: any) => {
            if (params.node.rowPinned) {
              return { backgroundColor: "var(--ag-header-background-color)", fontWeight: "bold" };
            }
            return undefined;
          }}
          onFilterChanged={recalculateTotal}
          onFirstDataRendered={recalculateTotal}
          onRowDataUpdated={recalculateTotal}
        />
      </Box>
      <Backdrop
        sx={{
          color: "common.white",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box>
        <ERPSnakBar
          open={alert.open}
          onClose={() => setAlert({ open: false, message: "", serverity: "" })}
          message={alert.message}
          serverity={alert.serverity}
        />
      </Box>
    </Box>
  );
}

export default SupplyStatus;
